import React, { useState } from 'react';
import { Button, Box, Divider, TextField, Menu, MenuItem, InputAdornment, IconButton, CircularProgress } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CommonModal from '../DocumentModals/CommonModal';
import {
  fetchProjectDocumentsAPI,
  fetchTechTransferDocumentsAPI,
  fetchCampaignDocumentsAPI,
  fetchMasterDocumentsAPI,
} from '../ProjectTabs/documentsAPI';
import { useStyles } from './Styles';

const DocumentNavigationTabs = ({ 
  projectId, 
  campaignId, 
  batchId, 
  operation = 'All',
  onDownloadSuccess,
  onDownloadError,
}) => {
  const classes = useStyles();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState('documentName');
  const [searchValue, setSearchValue] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingButtonIndex, setLoadingButtonIndex] = useState(null); // Track which button is loading

  const documentTabs = [
    'Project Documents',
    'Tech Transfer Documents',
    'Campaign Documents',
    'Master Documents',
  ];

  const handleButtonClick = async (index) => {
    setSelectedIndex(index);
    const tabName = documentTabs[index];
    setLoading(true);
    setLoadingButtonIndex(index); // Set which button is loading
    
    try {
      let response;
      let columns;
      let showUploadButton = false;
      let uploadButtonText = '';

      switch (tabName) {
        case 'Project Documents':
          if (!projectId) {
            console.error('Project ID is required for Project Documents');
            setLoading(false);
            return;
          }
          response = await fetchProjectDocumentsAPI(projectId);
          columns = ['Document Name', 'Document Type', 'Upload Date'];
          break;

        case 'Tech Transfer Documents':
          if (!projectId) {
            console.error('Project ID is required for Tech Transfer Documents');
            setLoading(false);
            return;
          }
          response = await fetchTechTransferDocumentsAPI(projectId);
          columns = ['Document Name', 'Document Type', 'Upload Date'];
          break;

        case 'Campaign Documents':
          // Use fallback campaign ID if not available (for testing)
          const campaignIdToUse = campaignId || "CAMP-001";
          if (!campaignId) {
            console.warn('Campaign ID not available, using fallback:', campaignIdToUse);
          }
          response = await fetchCampaignDocumentsAPI(campaignIdToUse);
          columns = ['Document Name', 'Document Type', 'Upload Date'];
          break;

        case 'Master Documents':
          // Use fallback batch ID if not available (for testing)
          const batchIdToUse = batchId || "BATCH-001";
          if (!batchId) {
            console.warn('Batch ID not available, using fallback:', batchIdToUse);
          }
          response = await fetchMasterDocumentsAPI(batchIdToUse, operation);
          columns = ['Document Name', 'Document Type', 'Version', 'Upload Date'];
          showUploadButton = true;
          uploadButtonText = 'Upload MBR';
          break;

        default:
          setLoading(false);
          return;
      }

      // Always open modal, even if response is empty or has errors
      if (response && response.status && response.data && Array.isArray(response.data)) {
        // Transform API response to table format
        const tableData = response.data.map(doc => {
          const row = {
            documentId: doc.documentId,
            documentName: doc.documentName,
            documentType: doc.documentType,
            uploadDate: doc.uploadDate,
            s3Url: doc.s3Url,
          };
          if (doc.version) {
            row.version = doc.version;
          }
          return row;
        });

        setModalData({
          title: `View ${tabName}`,
          columns: columns,
          tableData: tableData,
          showUploadButton: showUploadButton,
          uploadButtonText: uploadButtonText,
          documentType: tabName,
        });
        setOpenModal(true);
      } else {
        // Open modal with empty data if API fails or returns no data
        console.warn(`No data received for ${tabName}, opening modal with empty data`);
        setModalData({
          title: `View ${tabName}`,
          columns: columns,
          tableData: [],
          showUploadButton: showUploadButton,
          uploadButtonText: uploadButtonText,
          documentType: tabName,
        });
        setOpenModal(true);
      }
    } catch (error) {
      console.error(`Error fetching ${tabName}:`, error);
    } finally {
      setLoading(false);
      setLoadingButtonIndex(null); // Clear loading state
    }
  };

  const handleDropdownClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleInputClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (value) => {
    setSearchCriteria(value);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getSearchCriteriaLabel = () => {
    const labels = {
      documentName: 'Document Name',
      documentType: 'Document Type',
      uploadDate: 'Upload Date',
    };
    return labels[searchCriteria] || 'Document Name';
  };

  return (
    <div className={classes.root}>
      <Divider style={{ marginBottom: '0.5rem' }} />
      <div className={classes.container}>
        {/* UI-PD-Changes: document-category buttons (Project / Tech Transfer /
            Campaign / Master Documents) removed from the chevron-expanded view.
            Search box and Upload Documents are retained. Disabled via `false`
            so the block + its handlers stay for easy restore. */}
        {false && (
        <div className={classes.buttonsContainer}>
          {documentTabs.map((label, index) => (
            <Button
              key={index}
              className={classes.documentButton}
              onClick={() => handleButtonClick(index)}
              disabled={loadingButtonIndex !== null && loadingButtonIndex !== index}
              style={{
                position: 'relative',
                opacity: loadingButtonIndex === index ? 0.7 : 1,
              }}
            >
              {loadingButtonIndex === index ? (
                <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CircularProgress
                    size={16}
                    thickness={4}
                    style={{ color: '#1976d2' }}
                  />
                  <span>{label}</span>
                </Box>
              ) : (
                label
              )}
            </Button>
          ))}
        </div>
        )}
        <div className={classes.searchContainer}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && console.log('Search:', searchCriteria, searchValue)}
            onClick={handleInputClick}
            className={classes.searchInput}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownClick(e);
                    }}
                    style={{ padding: '4px', marginRight: '4px', cursor: 'pointer' }}
                  >
                    <ArrowDropDownIcon style={{ fontSize: '20px', color: '#000' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            getContentAnchorEl={null}
          >
            <MenuItem disabled style={{ color: '#757575', fontStyle: 'italic' }}>
              No option
            </MenuItem>
          </Menu>
          <Button
            className={classes.searchButton}
            onClick={() => console.log('Search:', searchCriteria, searchValue)}
          >
            <SearchIcon />
          </Button>
        </div>
        {/* UI-PD-Changes: Upload Documents button also removed from the
            chevron-expanded view — only the Search box is retained. Disabled
            via `false` so it stays for easy restore. */}
        {false && (
        <Button
          startIcon={<AddIcon />}
          className={classes.uploadButton}
          onClick={() => console.log('Upload Documents')}
        >
          Upload Documents
        </Button>
        )}
      </div>
      {modalData && (
        <CommonModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          title={modalData.title}
          tableData={modalData.tableData}
          columns={modalData.columns}
          showUploadButton={modalData.showUploadButton}
          uploadButtonText={modalData.uploadButtonText}
          showOkButton={true}
          modalType="documents"
          loading={loading}
          onDownloadSuccess={onDownloadSuccess}
          onDownloadError={onDownloadError}
        />
      )}
    </div>
  );
};

export default DocumentNavigationTabs;


